import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import StandingsGrid, { GridParticipant, GridPick, GridDay } from '../components/StandingsGrid'
import './StandingsPage.css'

// One-shot confetti burst that plays on first render, then cleans itself up.
function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const COLORS = ['#FFD700', '#FF6B00', '#4ade80', '#a5b4fc', '#f472b6', '#ffffff', '#fb923c']
    const TOTAL = 160
    const DURATION = 5000 // ms

    type Particle = {
      x: number; y: number; vx: number; vy: number
      size: number; color: string; rotation: number; rotSpeed: number
    }

    const particles: Particle[] = Array.from({ length: TOTAL }, () => ({
      x: Math.random() * canvas.width,
      y: -10 - Math.random() * 200,
      vx: (Math.random() - 0.5) * 2.5,
      vy: 2 + Math.random() * 4,
      size: 6 + Math.random() * 8,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.15,
    }))

    const start = performance.now()
    let raf: number

    function draw(now: number) {
      const elapsed = now - start
      const progress = Math.min(elapsed / DURATION, 1)
      ctx!.clearRect(0, 0, canvas!.width, canvas!.height)

      for (const p of particles) {
        p.x += p.vx
        p.y += p.vy
        p.rotation += p.rotSpeed
        p.vy += 0.05 // gravity

        ctx!.save()
        ctx!.globalAlpha = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1
        ctx!.translate(p.x, p.y)
        ctx!.rotate(p.rotation)
        ctx!.fillStyle = p.color
        ctx!.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
        ctx!.restore()
      }

      if (progress < 1) {
        raf = requestAnimationFrame(draw)
      } else {
        ctx!.clearRect(0, 0, canvas!.width, canvas!.height)
      }
    }

    raf = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', top: 0, left: 0,
        width: '100vw', height: '100vh',
        pointerEvents: 'none', zIndex: 9999,
      }}
    />
  )
}

type PoolTab = 'main' | 'redemption'

export default function StandingsPage() {
  const { participant: me, redemptionParticipant: myRedemption, activePool } = useAuth()
  const activeTab: PoolTab = activePool === 'redemption' ? 'redemption' : 'main'

  // Main pool data
  const [participants, setParticipants] = useState<GridParticipant[]>([])
  const [picks, setPicks] = useState<GridPick[]>([])
  const [days, setDays] = useState<GridDay[]>([])

  // Redemption pool data
  const [rParticipants, setRParticipants] = useState<GridParticipant[]>([])
  const [rPicks, setRPicks] = useState<GridPick[]>([])

  const [loading, setLoading] = useState(true)
  const fetchDataRef = useRef(fetchData)
  const fetchingRef = useRef(false)

  useEffect(() => {
    fetchData()
    const interval = setInterval(() => setDays(d => [...d]), 60_000)
    return () => clearInterval(interval)
  }, [])

  // Keep ref current after every render
  useEffect(() => { fetchDataRef.current = fetchData })

  // Re-fetch when the browser tab becomes visible or is restored from bfcache
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchDataRef.current()
    }
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) fetchDataRef.current()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])

  async function fetchData() {
    if (fetchingRef.current) return
    fetchingRef.current = true
    setLoading(true)
    const giveUp = setTimeout(() => setLoading(false), 8000)
    try {
      const [
        { data: participantsData },
        [{ data: picksPage1 }, { data: picksPage2 }],
        { data: daysData },
        { data: rParticipantsData },
        { data: rPicksData },
      ] = await Promise.all([
        // Main pool participants
        supabase
          .from('participants')
          .select('id, full_name, is_eliminated, is_paid, eliminated_on_date')
          .eq('is_paid', true)
          .eq('pool', 'main')
          .order('full_name', { ascending: true }),

        // All picks in two parallel pages to bypass the 1000-row server cap
        Promise.all([
          supabase
            .from('picks')
            .select('participant_id, game_date, result, is_auto_assigned, teams(name, seed)')
            .order('game_date', { ascending: true })
            .order('id', { ascending: true })
            .range(0, 999),
          supabase
            .from('picks')
            .select('participant_id, game_date, result, is_auto_assigned, teams(name, seed)')
            .order('game_date', { ascending: true })
            .order('id', { ascending: true })
            .range(1000, 1999),
        ]),

        // Tournament days
        supabase
          .from('tournament_days')
          .select('game_date, round_name, deadline')
          .order('game_date', { ascending: true }),

        // Redemption pool participants — HIDDEN (pool not running; queries preserved for future re-enable)
        Promise.resolve({ data: [] }),

        // Redemption picks — HIDDEN
        Promise.resolve({ data: [] }),
      ])

      const picksData = [...(picksPage1 || []), ...(picksPage2 || [])]
      setParticipants(participantsData || [])
      setPicks((picksData as any) || [])
      setDays(daysData || [])
      setRParticipants(rParticipantsData || [])
      // Filter picks to only redemption participants
      const rIds = new Set((rParticipantsData || []).map((p: any) => p.id))
      setRPicks(((rPicksData as any) || []).filter((pk: any) => rIds.has(pk.participant_id)))
    } catch (err) {
      console.error('fetchData error:', err)
    } finally {
      fetchingRef.current = false
      clearTimeout(giveUp)
      setLoading(false)
    }
  }

  // Main pool stats
  const totalPot = participants.length * 25
  const alive = participants.filter(p => !p.is_eliminated).length
  const eliminated = participants.filter(p => p.is_eliminated).length

  // Redemption pool stats
  const rAlive = rParticipants.filter(p => !p.is_eliminated).length
  const rEliminated = rParticipants.filter(p => p.is_eliminated).length

  // Redemption Island: only show days from Round of 32 onward (Mar 21+)
  const redemptionDays = days.filter(d => d.game_date >= '2026-03-21')

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>

  return (
    <div className="standings-page">
      <Confetti />

      <div className="winners-banner">
        <div className="winners-trophy">🏆</div>
        <div className="winners-text">
          <div className="winners-label">2026 Champions</div>
          <div className="winners-names">Nina Bohan &amp; Sean Guzzo</div>
        </div>
      </div>

      {activeTab === 'main' && (
        <>
          <div className="standings-stats">
            <div className="standings-stat">
              <div className="sstat-num">{alive}</div>
              <div className="sstat-label">Still Alive</div>
            </div>
            <div className="standings-divider" />
            <div className="standings-stat">
              <div className="sstat-num">{eliminated}</div>
              <div className="sstat-label">Eliminated</div>
            </div>
            <div className="standings-divider" />
            <div className="standings-stat">
              <div className="sstat-num">${totalPot}</div>
              <div className="sstat-label">Total Pot</div>
            </div>
            <div className="standings-divider" />
            <div className="standings-stat">
              <div className="sstat-num">{participants.length}</div>
              <div className="sstat-label">Entries</div>
            </div>
          </div>

          <StandingsGrid
            participants={participants}
            picks={picks}
            days={days}
            meId={me?.id}
            onRefresh={fetchData}
          />
        </>
      )}

      {false && activeTab === 'redemption' && ( /* REDEMPTION HIDDEN */
        <>
          {rParticipants.length === 0 ? (
            <div className="redemption-empty">
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏝️</div>
              <h2>Redemption Island</h2>
              <p>No registered participants yet. Check back after Round of 64 eliminations register.</p>
            </div>
          ) : (
            <>
              <div className="standings-stats">
                <div className="standings-stat">
                  <div className="sstat-num">{rAlive}</div>
                  <div className="sstat-label">Still Alive</div>
                </div>
                <div className="standings-divider" />
                <div className="standings-stat">
                  <div className="sstat-num">{rEliminated}</div>
                  <div className="sstat-label">Eliminated</div>
                </div>
                <div className="standings-divider" />
                <div className="standings-stat">
                  <div className="sstat-num">{rParticipants.length}</div>
                  <div className="sstat-label">Entries</div>
                </div>
              </div>

              <StandingsGrid
                participants={rParticipants}
                picks={rPicks}
                days={redemptionDays}
                meId={myRedemption?.id}
                onRefresh={fetchData}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
